/**
 * Design from https://www.dropbox.com/home/scoreboard3/scorecard/edit?preview=scorecard-edit-Use-as-Template.psd
 */

import Backbone from 'backbone'

import template from './org-paste-destination-form.hbs'
import OrgGardener from 'scoreboard/organizations/organizations-gardener'
import globals from 'scoreboard/components/globals'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import Finder from 'scoreboard/components/finder'
import FormView from 'scoreboard/components/form-view'
import isInSubtree from 'scoreboard/components/is-in-subtree'
import Tooltip from 'scoreboard/components/tooltip'
import BoxOptions from 'scoreboard/components/box-options'
import projects from 'scoreboard/projects/projects'
import highlight from 'scoreboard/components/highlight'
import treeSomeAncestors from 'scoreboard/components/tree-some-ancestors'
import treeSomeDescendants from 'scoreboard/components/tree-some-descendants'
import TemplateNamingPanel from 'scoreboard/templates/template-naming-panel'
import resizeDialogContent from 'scoreboard/components/resize-dialog-content'

/**
 * Superclass view used when pasting an organization; extensions exist for:
 *   a standard copy in same or new project
 *   a template copy in same project
 *   a move (into new project)
 *
 * Gives the user an org tree to choose the parent under which the new copy will live.
 */
var OrgPasteDestinationForm = Backbone.View.extend({

	// this.model is the organization that will be copied
	// this.options.orgId is an optional org ID to use to initialize the tree

	tagName: 'form',
	className: 'org-paste-destination-form',

	// dialog title/header (can be string or function)
	title: null,

	// array of banner messages to display at the top (optional)
	messages: null,
	preMessages: null, // preformatted, won't be escaped

	// function to call when form is submitted (validation is done first)
	submit: null,

	showProjectSelector: true,

	/**
	 * @param {Number} [options.projectId=projects.id]
	 * @param {Number} [options.orgId=globals.orgId]
	 * @param {String} [options.copyType] (single, tree, dataset)
	 */
	initialize: function(options) {
		this.options = options || {}
		// TODO: If we're saving to a project which is pending (not yet chosen but it's orgs are visible)
		// the copies will save correctly to the org, but they will not populate automatically
		// like when the project/org are fully chosen and switched to.
		this.projectId = parseInt(this.options.projectId || projects.pendingProjectId || projects.id)
		this.finder = new Finder()
		this.finder.on('find', this._onFind.bind(this))

		var PasteFormView = FormView.extend({
			test: this.test.bind(this),
			validate: this.validate.bind(this)
		})

		this.formView = new PasteFormView({
			el: this.el,
			submit: this.submit.bind(this),
			allowCleanSubmission: () => this.destinationOrg()
		})

		this._restrictionMessages = {}
	},

	copyOptions() {
		return [
			{
				value: 'single',
				title: i18n.get('template.paste.singleOrg', '<q>' + _.escape(this.model.get('label')) + '</q>'),
				icon: 'copy-organization'
			},
			{
				value: 'tree',
				title: i18n.get('template.paste.entireTree', '<q>' + _.escape(this.model.get('label')) + '</q>'),
				icon: 'copy-organization-descendants'
			}
		]
	},

	render: function() {
		this.$el.empty().append(template(this.context()))

		// Allow the copy options to render first, so components that needs its
		// selected value don't break.
		this._renderCopyOptions()
		this._renderOrg(this.options.orgId || globals.orgId)

		if (this.options.wizard) {
			// we need the user to click on Next so we can send out a request
			// and validate the org selection
			this.options.wizard.lastNavigable = 1
			this.wizardProgress = this.options.wizard.wizardProgress()
			this.$('.button-primary').before(this.wizardProgress.el)
			this.wizardProgress.render()
		} else {
			// If this isn't a wizard, then we allow the user to set the copy
			// organization's name on this dialog.
			this.renderOrgNamePanel()
			this.listenTo(this.copyOptionsView, 'select', this
				.updateOrgNamePanel.bind(this))
		}

		this.formView.start()

		return this
	},

	renderOrgNamePanel: function () {
		const orgName = this.model.get('label')
		this.orgNamePanel = new TemplateNamingPanel({
			copyType: this.copyType(),
			section: 'organizations',
			label: orgName,
			rollupTreeGroupLabel: orgName,
			inputLabels: this.inputLabels,
			heading: this.copyType() === 'single' ?
				i18n.get('template.paste.suffix.header.organization') :
				i18n.get('template.paste.suffix.header.organizations')
		})
		this.$('.org-name-panel').empty().append(this.orgNamePanel.el)
		this.orgNamePanel.render()
	},

	updateOrgNamePanel: function () {
		const selectedCopyType = this.copyType()
		if (!this.options.wizard && (selectedCopyType === 'single' ||
			selectedCopyType === 'tree')) {
			this.renderOrgNamePanel()
		}
	},

	resize(maxHeight) {
		let container = this.$('.org-selection')
		resizeDialogContent({
			dialog: this.el,
			maxHeight,
			target: container.find('.panel'),
			minHeight: 250,
			container: container[0]
		})
	},

	_renderCopyOptions() {
		const availableOptions = this.copyOptions()
		if (!_.isEmpty(availableOptions)) {
			this.copyOptionsView = new BoxOptions({availableOptions}).render()
			this.$('.org-copy-options').append(this.copyOptionsView.el)
			if (this.options.copyType) {
				this.copyOptionsView.val(this.options.copyType)
			}
			this._updateCopyTypeOptions()
			this.listenTo(this.copyOptionsView, 'select', this.change
				.bind(this))
			this.listenTo(this.copyOptionsView, 'select:disabled', options => {
				this._showRestrictionTooltip(options)
			})
		} else {
			this.$('.org-copy-options, .paste-options').hide()
		}
	},

	/**
	 * @param {Number} [orgId]
	 */
	_renderOrg(orgId) {
		this.ready = false
		this.orgGardener = new OrgGardener({
			showProjectSelector: this.showProjectSelector,
			projectId: this.projectId,
			id: orgId,
			section: this.model.section,
			permissions: true
		})
		this.$('.org-tree-container').empty().append(this.orgGardener.el)
		this.orgGardener.render()

		this.$('.tree-container').addClass('tree-container-light')
		this.orgGardener.on('select', this._onOrgSelect.bind(this))
		this.orgGardener.once('node', node => {
			// Nix the noOrgsViewable appearance when we know we have nodes
			this.toggleNoOrgsViewable(true)
		})
		this.orgGardener.on('node', node => {
			if (node.id == orgId) {
				this._onOrgSelect(node)
			}
		})

		this.orgGardener.on('error', error => {
			// When we don't have any orgs to display, change the gardener appearance
			// to reflect that there aren't any visible
			if (_.get(error, 'statusCode') == 404) {
				this.toggleNoOrgsViewable(false)
			}
		})

		this.orgGardener.on('projectId', id => {
			this.projectId = id
			this._renderOrg()
			this._updateCopyTypeOptions()
		})

		this.orgGardener._onceTreeLoaded(() => {
			this.ready = true
			if (this.projectId === this.model.get('projectId')) {
				this._sourceUnreplicatedDescendants = treeSomeDescendants(this.orgGardener.tree,
					this.model.get('id'), node => !node.replicated)
			}
			this.change()
		})
	},

	// Toggles whether the .no-viewable-org messages are showing or not.
	toggleNoOrgsViewable(viewable) {
		// Toggle visibility according to whether there are organizations to show
		this.orgGardener.$('.finder input, .expand-collapse').attr('disabled', !viewable)
		this.$('.org-message, .org-label').toggle(viewable)
		this.$('.no-viewable-org-message').toggle(!viewable)
		if (!viewable) {
			// Show the 'This project has no Organizations' message
			this.orgGardener.displayNoOrgs()
			// Show the 'Make [org] the root organization in [project]' message
			var org = this.model.get('label')
			var project = projects.get(this.projectId).get('label')
			this.$('.no-viewable-org-message').html(i18n.get('common.project.makeOrgRoot', `<b>${org}</b>`, `<b>${project}</b>`))
		}
	},

	copyType: function () {
		return this.copyOptionsView ? this.copyOptionsView.val() : this.options.copyType
	},

	destinationOrg: function () {
		const emptyProject = _.size(_.get(this.orgGardener, 'tree.nodes')) === 0
		if (emptyProject) {
			// No orgs in the tree indicates the user wants to move as the root org in a project
			return {
				projectId: this.projectId,
				newOrgRoot: true,
				cascade: true // Bypass validation
			}
		} else {
			return _.result(this.orgGardener, 'tree.selected')
		}
	},

	change: function (node) {
		if (node && node.label) {
			this.$('.org-message').show()
			this.$('.org-label').text(node.label)
		} else {
			this.$('.org-message').hide()
			this.$('.org-label').text(i18n.get('common.organization.select'))
		}
		this.formView.clearErrors()
		this.formView.change()
		this._updateCopyTypeOptions()

		if (this.options.wizard) {
			this.options.wizard.copyType(this.copyType())
		}

		this.trigger('change')
	},

	cannotCopyUnderDescendentError() {
		var key = this.options.copy ? 'template.paste.cannotCopyTreeToDescendant' :
			'template.paste.cannotMoveTreeToDescendant'
		return i18n.get(key)
	},

	context: function() {
		const messages = _.result(this, 'messages')
		const preMessages = _.result(this, 'preMessages')
		return {
			objectLabel: this.model.get('label'),
			heading: _.result(this, 'title'),
			anyMessages: messages || preMessages,
			messages,
			preMessages,
			orgMessage: this._orgMessage(),
			primaryButtonLabel: _.result(this, 'primaryButtonLabel'),
			pasteOptionsMessage: this._pasteOptionsMessage()
		}
	},

	_orgMessage() {
		return i18n.get('template.createNewScorecardIn', i18n.get('common.organization.organization'))
	},

	_pasteOptionsMessage() {
		return i18n.get('common.copy.also', i18n.get('common.copy'))
	},

	_onFind: function(text) {
		this.orgGardener.tree.search(text)
	},

	_onOrgSelect: function(node) {
		this.change(node)
	},

	// For a given copy mode, returns an appropriate error message if
	// that mode can't be used for the source org and the currently selected destination
	getRestriction: function (copyType) {

		let node = _.result(this.orgGardener, 'tree.selected')
		if (copyType === 'tree') {
			if (node) {
				// don't allow combining the "copy including subtree" option with
				// pasting in that same subtree
				if (isInSubtree(this.orgGardener.tree, node.id, this.model.id)) {
					return this.cannotCopyUnderDescendentError()
				}

				if (!this.model.get('cascade')) {
					var sourceLabel = highlight(this.model.get('label'))
					return i18n.get('common.organization.noCascadeSource', sourceLabel)
				}
			}
		}

		//#21335 prevent unreplicated org copying underneath a cascaded replicated org
		if (node) {
			let targetAncestorReplicatedCascade = node.replicatedCascaded ||
				treeSomeAncestors(this.orgGardener.tree, node.id, parent => {
					return parent.replicatedCascaded
				})
			if (targetAncestorReplicatedCascade) {
				if (!node.replicated || this._sourceUnreplicatedDescendants) {
					let orgWithoutRep = this.model.get('label')
					let orgWithRep = node.label
					let msgCode = 'common.error.sourceOrgRequiresReplication'
					return i18n.get(msgCode, highlight(orgWithoutRep), highlight(orgWithRep))
				}
			}
		}

		return null
	},

	test: function () {
		var errors = []

		let copyType = this.copyType()
		if (copyType) {
			var restriction = this.getRestriction(copyType)
			if (restriction) {
				errors.push(restriction)
			}
		} else {
			errors.push(i18n.get('template.paste.mustPickCopyOption'))
		}

		if (!this.ready) {
			// Must wait for the org gardener to be rendered
			// (It will auto-select an org)
			errors.push(i18n.get('common.organization.select'))
		}  else {
			var destination = this.destinationOrg()
			if (!destination) {
				errors.push(i18n.get('common.organization.select'))
			} else if (!destination.cascade) {
				var destinationLabel = highlight(destination.label)
				errors.push(i18n.get('common.organization.notauthorizedDestination', destinationLabel))
			}
		}

		return errors.length === 0 ? null : errors
	},

	validate: function () {
		var errors = this.test()
		if (errors && errors.length > 0) {
			this.formView.errorMessages(errors)
		} else {
			this.formView.clearErrors()
		}
		return errors
	},

	// Updates the "Copy Type" selection and the corresponding tooltip based on the
	// currently selected destination
	_updateCopyTypeOptions: function () {
		if (this.copyOptionsView) {
			let currentCopyType = this.copyType()

			_.forEach([ 'single', 'tree', 'dataset' ], copyType => {
				let restriction = this.getRestriction(copyType)

				// enable copyType if no restriction message
				this.copyOptionsView.toggleEnabled({ value: copyType}, !restriction)

				// store restriction message for later use in tooltip
				this._restrictionMessages[copyType] = restriction
			})

			if (!currentCopyType || this._restrictionMessages[currentCopyType]) {
				let firstValid = _([ 'single', 'tree', 'dataset' ]).find(copyType => {
					return !this._restrictionMessages[copyType]
				})

				if (firstValid) {
					this.copyOptionsView.val(firstValid)
				}
			}
		}
	},

	_showRestrictionTooltip: function ({ option, optionEl }) {
		this._removeRestrictionTooltip()

		let attemptedType = option.value
		let restriction = this._restrictionMessages[attemptedType]

		if (!restriction) {
			return
		}

		this._restrictionTooltip = new Tooltip({
				attachTo: optionEl,
				content: restriction,
				placement: 'above',
				arrow: 'center'
			})
			.render()
			.on('remove', function () {
				delete this._restrictionTooltip
			}.bind(this))
	},

	_removeRestrictionTooltip: function () {
		this._restrictionTooltip && this._restrictionTooltip.remove()
		delete this._restrictionTooltip
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)

		_.invokeMap([this.orgGardener, this.wizardProgress,
			this.formView, this.copyOptionsView, this.orgNamePanel], 'remove')

		this._removeRestrictionTooltip()
	}

})

export default OrgPasteDestinationForm

OrgPasteDestinationForm.__filename__ = 'organizations/org-paste-destination-form.js'
